/**
 * errorReporter — best-effort client-error telemetry (stability, 2026-06-14).
 *
 * Background: until now a render crash on any page blanked the WHOLE screen
 * (white) and NOTHING was logged anywhere — a real bug (a non-string field
 * crashed a list page) stayed invisible because no signal ever left the
 * browser. This module is the «signal out» half: it POSTs captured errors to
 * the backend so they show up in our logs / dashboards.
 *
 * Hard constraints (a telemetry pipe must never become a source of failures):
 *   • NEVER throws — every public call is wrapped; a reporter bug must not take
 *     down the page it is trying to instrument.
 *   • NEVER loops — the fetch itself is fire-and-forget and its own rejection
 *     is swallowed, so a failing /client-errors endpoint can't trigger an
 *     unhandledrejection that we'd report again, and again…
 *   • DEDUPE — the same (message+source) within ~30s is reported once (a render
 *     loop can fire the same error dozens of times per second).
 *   • RATE-LIMIT — at most ~10 reports/minute leave the browser (drop the rest)
 *     so a pathological page can't flood the backend.
 *
 * Transport: a plain `fetch` with `keepalive:true` (not the axios apiClient) —
 * keepalive lets the report survive a navigation/unload that races the crash,
 * and staying off axios avoids the 401-redirect interceptor firing for a
 * telemetry call. Base path matches the app API base ("/api").
 */

/** Where reports go. The api base is "/api" (nginx-proxied in prod, vite proxy in dev). */
const REPORT_URL = "/api/v1/client-errors";

/** Dedupe window — collapse identical (message+source) within this many ms. */
const DEDUPE_WINDOW_MS = 30_000;

/** Rate limit — at most this many reports per ROLLING minute. */
const RATE_LIMIT_MAX = 10;
const RATE_LIMIT_WINDOW_MS = 60_000;

export interface ClientErrorInput {
  message: string;
  stack?: string;
  /** Vue's component-trace string (from errorHandler/onErrorCaptured `info`). */
  info?: string;
  /** Origin tag: "vue" | "vue-render" | "window.onerror" | "unhandledrejection" | … */
  source?: string;
}

/** Recently-seen keys → last-sent timestamp (for the dedupe window). */
const recentKeys = new Map<string, number>();
/** Timestamps (ms) of reports sent within the rolling rate-limit window. */
let sentTimestamps: number[] = [];

/** Build the dedupe key from the load-bearing identity of an error. */
function keyOf(input: ClientErrorInput): string {
  return `${input.source ?? ""}::${input.message ?? ""}`;
}

/**
 * Current router path, read defensively. The router is imported lazily (it is
 * itself part of the app graph) and any failure here is non-fatal — telemetry
 * must work even if routing is the thing that's broken.
 */
function currentRoute(): string {
  try {
    // Read from the live location rather than importing the router (which could
    // be mid-construction or the very thing that crashed). Hash+search included
    // so the report points at the exact view.
    if (typeof window !== "undefined" && window.location) {
      return window.location.pathname + window.location.search + window.location.hash;
    }
  } catch {
    /* ignore — fall through */
  }
  return "";
}

/** Has the dedupe/rate-limit budget already been spent for this error/window? */
function shouldDrop(input: ClientErrorInput, now: number): boolean {
  // 1) DEDUPE — identical (source+message) seen within the window → drop.
  const key = keyOf(input);
  const last = recentKeys.get(key);
  if (last !== undefined && now - last < DEDUPE_WINDOW_MS) {
    return true;
  }

  // 2) RATE-LIMIT — prune anything older than the window, then check the cap.
  sentTimestamps = sentTimestamps.filter((t) => now - t < RATE_LIMIT_WINDOW_MS);
  if (sentTimestamps.length >= RATE_LIMIT_MAX) {
    return true;
  }

  return false;
}

/**
 * Report a client error to the backend. Best-effort and SILENT on failure —
 * safe to call from anywhere (Vue errorHandler, window.onerror,
 * unhandledrejection, an error boundary). Never throws, never loops.
 */
export function reportClientError(input: ClientErrorInput): void {
  try {
    const now = Date.now();
    if (shouldDrop(input, now)) return;

    // Reserve the budget BEFORE the async send so concurrent synchronous calls
    // (a render loop emitting the same error N times in one tick) collapse.
    recentKeys.set(keyOf(input), now);
    sentTimestamps.push(now);

    // Opportunistic cleanup so the dedupe map can't grow without bound on a
    // long-lived tab that hits many distinct errors.
    if (recentKeys.size > 200) {
      for (const [k, ts] of recentKeys) {
        if (now - ts >= DEDUPE_WINDOW_MS) recentKeys.delete(k);
      }
    }

    const payload = {
      message: input.message,
      stack: input.stack,
      info: input.info,
      source: input.source,
      route: currentRoute(),
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
      build: import.meta.env.VITE_BUILD ?? "dev",
    };

    // Fire-and-forget. `.catch` here is CRITICAL: without it a rejected fetch
    // becomes an unhandledrejection → which our global handler would report →
    // which fetches again → infinite loop. Swallow it.
    void fetch(REPORT_URL, {
      method: "POST",
      keepalive: true,
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }).catch(() => {
      /* telemetry endpoint down — intentionally ignored, see above */
    });
  } catch {
    /* the reporter itself must never throw — swallow everything */
  }
}

/**
 * Test-only: reset the in-memory dedupe / rate-limit state so specs don't bleed
 * into each other. Not part of the runtime contract.
 */
export function __resetErrorReporterState(): void {
  recentKeys.clear();
  sentTimestamps = [];
}
